import React, { useEffect, useState } from 'react'; 
import styled from 'styled-components';
import { useCryptoStore } from '../../store/cryptoStore';
import TickerCard from './TickerCard';

const CryptoTicker: React.FC = () => {
  const { prices, topPrices, isConnected, error, startStream } = useCryptoStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortedSymbols, setSortedSymbols] = useState<string[]>([]);

  useEffect(() => {
    startStream();
    return () => useCryptoStore.getState().stopStream();
  }, [startStream]);

  useEffect(() => {
    if (searchTerm.length >= 1) {
      // Arama yapılıyorsa tüm coinlerde ara
      const symbols = Object.keys(prices)
        .filter(symbol => 
          symbol.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort();
      setSortedSymbols(symbols);
    } else {
      // Arama yoksa top 12'yi göster
      setSortedSymbols(Object.keys(topPrices));
    }
  }, [searchTerm, prices, topPrices]);

  return (
    <TickerContainer>
      <TickerHeader>
        <StatusSection>
          <ConnectionStatus $isConnected={isConnected}>
            {isConnected ? '🟢 Canlı Veri Akışı' : '🔴 Bağlantı Bekleniyor'}
          </ConnectionStatus>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </StatusSection>
        
        <SearchSection>
          <SearchInput
            type="text"
            placeholder="Proje ara... (örn: BTC)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchIcon>🔍</SearchIcon>
        </SearchSection>
      </TickerHeader>

      {isConnected ? (
        <>
          <ResultCount>
            {searchTerm 
              ? `${sortedSymbols.length} proje bulundu` 
              : 'En Yüksek Hacimli 12 Proje'}
          </ResultCount>
          
          <TickerGrid>
            {sortedSymbols.map(symbol => (
              <TickerCard 
                key={symbol} 
                data={searchTerm ? prices[symbol] : topPrices[symbol]}
              />
            ))}
          </TickerGrid>
          
          
          {searchTerm && sortedSymbols.length === 0 && (
            <NoResults>
              "{searchTerm}" ile eşleşen proje bulunamadı
            </NoResults>
          )}
        </>
      ) : (
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Veriler yükleniyor...</LoadingText>
        </LoadingContainer>
      )}
    </TickerContainer>
  );
};

const SearchSection = styled.div`
  position: relative;
  width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 16px;
  padding-right: 40px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #E2E8F0;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3B82F6;
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
`;

const ResultCount = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 8px 0;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 32px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
`;

const TickerContainer = styled.div`
  padding: 20px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  min-height: 200px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.2);
  }
`;

const TickerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StatusSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TickerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
  margin-top: 20px;
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const ErrorMessage = styled.div`
  color: #F44336;
  padding: 8px 16px;
  background: rgba(244, 67, 54, 0.1);
  border-radius: 20px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '⚠️';
  }
`;

const ConnectionStatus = styled.div<{ $isConnected: boolean }>`
  padding: 8px 16px;
  border-radius: 20px;
  background: ${props => props.$isConnected ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)'};
  color: ${props => props.$isConnected ? '#4CAF50' : '#F44336'};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  border-top-color: #3B82F6;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
`;

export default CryptoTicker;